import { computed, nextTick, ref } from 'vue'
import { freezeObject, getBooleanFromValue, isFunction, isSet, isEmpty } from '@/global/utils/utils'
import { FormDataBuilder } from '@/global/services/helpers/formDataBuilder'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { useTableService } from '@/global/composables/table/useTableService'

const TAB_KEY = 'lager'
export function useInventory (options) {
  const { buildConfig } = useTableService()
  const {
    tableGridContentComponent,
    trans,
    route,
    additionalFiltersComponent,
    openManageInventoryDialogFn,
    closeManageInventoryDialogFn,
    openEditInventoryDialogFn,
    closeEditInventoryDialogFn,
    editInventoryDialogState,
    setEditLoaderFn,
    setEditValidationErrorsFn,
    manageInventoryDialogState,
    setManageInventoryValidationErrorsFn
  } = options || {}

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: `products/${route.params.id}/inventory`
    }
  }))

  const productId = route.params.id
  const useSerialNumber = ref(false)

  const createNewLagerCallback = async () => {
    try {
      const formDataSource = manageInventoryDialogState?.config?.formData

      if (isEmpty(formDataSource)) {
        return
      }

      const formData = new FormData()
      for (const key in formDataSource) {
        formData.append(key, formDataSource[key])
      }

      await api('road-maintenance').post(`products/${productId}/inventory`, formData)
      closeManageInventoryDialogFn()
      refreshData()
    }
    catch (exception) {
      const { errors } = exception?.response?.data || {}
      setManageInventoryValidationErrorsFn(errors)
    }
  }

  const onAddProductToStock = () => {
    const config = {}
    config.title = trans('road-maintenance/inventory/product.adding_product_to_stock')

    const fields = [
      {
        key: 'geolocation_id',
        label: trans('road-maintenance.excavation_modal_location_btn'),
        type: 'autocomplete',
        autofocus: true,
        openMenuAutomatically: true,
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'work-order-geolocations/autocomplete',
          custom_display_value: (item) => {
            return item?.description || null
          }
        }
      },
      {
        key: 'quantity',
        label: trans('fleet/documents/prl_cargo.amount'),
        initial_value: useSerialNumber.value ? 1 : undefined,
        disabled: useSerialNumber.value,
        field_type: useSerialNumber.value ? 'text' : 'number'
      }
    ]

    if (useSerialNumber.value) {
      fields.push({
        key: 'serial_number',
        label: trans('fleet/documents/prl_cargo.sr_no')
      })
    }

    config.fields = fields

    // callback function for adding
    config.positiveButton = {
      text: trans('road-maintenance/inventory/product.add'),
      callback: createNewLagerCallback
    }

    // callback function for cancel
    config.negativeButton = {
      text: trans('base.cancel')
    }

    config.formData = {}
    config.validationErrors = {}

    openManageInventoryDialogFn(config)
  }

  const searchAndButtonsConfig = computed(() => ({
    additional_buttons: [
      {
        type: 'base',
        label: trans('road-maintenance/inventory/product.move'),
        prependIcon: 'mdi-cart-arrow-right',
        click: () => onMoveProduct(null, true)
      }
    ],
    newClick: onAddProductToStock
  }))

  const headers = ref([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('fleet/communication.location'),
      key: 'location',
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory/product.quantity'),
      key: 'quantity',
      style: {
        textAlign: 'center'
      },
      always_visible: true
    },
    {
      label: trans('fleet/documents/prl_cargo.sr_no'),
      key: 'serial_number',
      style: {
        textAlign: 'center'
      },
      always_visible: true
    }
  ])

  const refreshData = () => {
    const { fetchData } = registeredTableHandlers?.[TAB_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }

    fetchData()
  }

  const onLagerEditConfirmation = async () => {
    try {
      const { quantity } = editInventoryDialogState?.formData || {}
      setEditLoaderFn(true)
      if (!isSet(quantity)) {
        setEditValidationErrorsFn({
          quantity: trans('validation/no_attributes.required')
        })
        return
      }

      const formData = new FormDataBuilder().build(editInventoryDialogState?.formData)
      formData.append('_method', 'PATCH')

      await api('road-maintenance').post(`products/${productId}/inventory/update-quantity`, formData)

      closeEditInventoryDialogFn()
      refreshData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setEditLoaderFn(false)
    }
  }

  const onLagerEdit = (event) => {
    const {
      quantity,
      location,
      product_item_id: productItemId
    } = event?.data || {}

    const config = {
      formData: {
        quantity,
        geolocation_id: location?.id,
        product_item_id: productItemId
      },
      validationErrors: {},
      showLoader: false,
      positiveButtonText: trans('base.confirm'),
      negativeButtonText: trans('base.cancel'),
      title: trans('road-maintenance/inventory/product.editing_lager'),
      onConfirm: onLagerEditConfirmation
    }

    openEditInventoryDialogFn(config)
  }

  const moveProductCallback = async () => {
    try {
      const formDataSource = manageInventoryDialogState?.config?.formData

      if (isEmpty(formDataSource)) {
        return
      }

      const formData = new FormData()
      formData.append('_method', 'PATCH')

      for (const key in formDataSource) {
        formData.append(key, formDataSource[key])
      }

      await api('road-maintenance').post(`products/${productId}/inventory`, formData)
      closeManageInventoryDialogFn()
      refreshData()
    }
    catch (exception) {
      const { errors } = exception?.response?.data || {}
      setManageInventoryValidationErrorsFn(errors)
    }
  }

  const onMoveProduct = (event, fromGlobalBtn = false) => {
    const { id: fromLocation } = event?.data?.location || {}
    const { product_item_id: productItemId } = event?.data || {}
    const formData = {}

    let fields = [
      {
        key: 'old_geolocation_id',
        label: trans('road-maintenance/inventory/product.from_location'),
        type: 'autocomplete',
        initial_value: fromLocation,
        disabled: !fromGlobalBtn,
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'work-order-geolocations/autocomplete',
          custom_display_value: (item) => {
            return item?.description || null
          }
        }
      },
      {
        key: 'new_geolocation_id',
        label: trans('road-maintenance/inventory/product.to_location'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'work-order-geolocations/autocomplete',
          custom_display_value: (item) => {
            return item?.description || null
          }
        }
      },
      {
        key: 'quantity',
        label: trans('fleet/documents/prl_cargo.amount'),
        initial_value: useSerialNumber.value ? 1 : undefined,
        disabled: useSerialNumber.value,
        field_type: useSerialNumber.value ? 'text' : 'number'
      }
    ]

    if (useSerialNumber.value) {
      fields = [
        {
          key: 'product_item_id',
          label: trans('road-maintenance/work_orders.serial_number'),
          type: 'autocomplete',
          initial_value: productItemId,
          disabled: !fromGlobalBtn,
          autocomplete_options: {
            module: 'road-maintenance',
            route: 'product-items/autocomplete',
            custom_display_value: (item) => {
              return item?.serial_number || null
            }
          },
          onChange: (event) => {
            const { geolocation_id: geolocationId } = event?.selectedItem || {}
            if (!geolocationId) {
              return
            }
            formData.old_geolocation_id = geolocationId
          }
        },
        ...fields
      ]
    }

    const config = {}
    config.title = trans('road-maintenance/inventory/product.moving_product')
    config.fields = fields
    config.formData = formData
    config.validationErrors = {}
    config.positiveButton = {
      text: trans('road-maintenance/inventory/product.move'),
      callback: moveProductCallback
    }
    config.negativeButton = {
      text: trans('base.cancel')
    }

    openManageInventoryDialogFn(config)
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onLagerEdit),
    {
      label: trans('road-maintenance/inventory/product.move'),
      icon: 'mdi-cart-arrow-right',
      click: onMoveProduct
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'location',
        label: trans('fleet/communication.location'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: `products/${productId}/inventory/locations`
        }
      }
    ]
  }))

  const onDataLoad = (event) => {
    const { data, updateHeaders } = event || {}
    const { meta } = data || {}
    useSerialNumber.value = getBooleanFromValue(meta?.use_serial_number)

    if (useSerialNumber.value) {
      return
    }

    const filteredHeaders = headers.value.filter(header => header.key !== 'serial_number')

    if (filteredHeaders.length === headers.value.length) {
      return
    }

    headers.value = filteredHeaders

    if (isFunction(updateHeaders)) {
      nextTick(() => {
        updateHeaders(filteredHeaders)
      })
    }
  }

  const cellsConfig = freezeObject({
    location: {
      custom_display_value: (event) => {
        return event?.location?.name || ''
      }
    },
    quantity: {
      style: {
        textAlign: 'center'
      }
    },
    serial_number: {
      style: {
        textAlign: 'center'
      }
    }
  })

  return computed(() => ({
    title: trans('road-maintenance/inventory/product.lager'),
    key: TAB_KEY,
    tabContent: [
      {
        component: tableGridContentComponent,
        ...buildConfig({
          key: TAB_KEY,
          apiConfig: apiConfig.value,
          rowDblClick: onLagerEdit,
          onDataReceivedWithParams: onDataLoad,
          title_and_back_config: {
            title: trans('road-maintenance/inventory/product.lager'),
            showBackButton: {
              uiViewName: 'TemporaryRegulationsProducts'
            }
          },
          customFiltersConfig: {
            component: additionalFiltersComponent,
            visible: useSerialNumber.value
          },
          search_and_buttons_config: searchAndButtonsConfig.value,
          tableConfig: {
            cellsConfig,
            headers: headers.value,
            actionsConfig: actionsConfig.value,
            filtersConfig: filtersConfig.value
          }
        })
      }
    ]
  }))
}
